import { Box, Card, CardContent, Typography } from "@mui/material";
import React from "react";
import { useOptionalConfig } from "../../contexts/ConfigContext";

const DEFAULT_TITLE = "CultureMap";
const DEFAULT_LOGO = "icons/lock.png";

/**
 * Full-screen block shown when the app is opened on a phone-sized viewport.
 * Can run with or without ConfigProvider: when inside providers (e.g. after
 * login), uses config for app name and logo; when not (e.g. not logged in),
 * uses defaults so the block shows immediately with no config load or login.
 * The login screen is never shown on phone — users must use a tablet or
 * laptop or computer with a big screen to sign in. Tablets (≥768px) are allowed.
 */
const MobileBlockScreen: React.FC = () => {
  const config = useOptionalConfig();
  const title: string = config?.homePageTitle ?? DEFAULT_TITLE;
  const logoUrl: string = config?.loginImagePath ?? DEFAULT_LOGO;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#fff",
        color: "#111",
        p: 2,
        zIndex: 9999,
      }}
    >
      <Card
        sx={{
          width: "90vw",
          maxWidth: "480px",
          overflow: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "linear-gradient(160deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
          borderRadius: "16px",
          transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          "&:hover": {
            boxShadow: "0 12px 48px rgba(0, 0, 0, 0.5)",
            border: "1px solid rgba(255, 255, 255, 0.25)",
          },
        }}
      >
        <CardContent sx={{ padding: "24px !important" }}>
          <Typography
            variant="h1"
            component="h2"
            gutterBottom
            align="center"
            sx={{
              fontSize: "1.8em",
              margin: "0.6em 0",
              fontWeight: 700,
              color: "#fff",
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
              letterSpacing: "0.02em",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img
              src={logoUrl}
              alt=""
              style={{
                width: "160px",
                maxWidth: "100%",
                padding: "8px",
                filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))",
                borderRadius: "12px",
              }}
            />
          </Box>
          <Typography
            variant="body1"
            align="center"
            sx={{
              color: "rgba(255, 255, 255, 0.95)",
              lineHeight: 1.6,
              fontSize: "0.95rem",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.4)",
            }}
          >
            This app is not yet supported on phones. There is too much content to
            fit on a small screen, and we want to avoid sensitive data being too
            easily accessible on handheld devices. Please use a laptop or a computer with a big screen
            for the best experience.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MobileBlockScreen;
